/**
 * @since 2025-02-19
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiMaximize2, FiPause, FiPlay } from "react-icons/fi";
import { getConfig } from "../../../../getConfig";
import "./LogViewer.scss";

const { BACKEND_WS_URL } = getConfig();

const LOG_LEVELS = {
  DEBUG: { color: "#008b8b" },
  INFO: { color: "#b0b0b0" },
  WARN: { color: "#b8860b" },
  ERROR: { color: "#ff6347" },
  CRITICAL: { color: "#8b008b" },
};

const LEVEL_PRIORITY = {
  ALL: -1,
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  CRITICAL: 4,
};

function determineLogLevel(text) {
  for (const level of Object.keys(LOG_LEVELS)) {
    if (text.includes(level)) {
      return level;
    }
  }
  return "INFO";
}

const LogViewer = ({ job, idToken, listLogFiles, onExpandClick, onAuthError }) => {
  const [logs, setLogs] = useState([]);
  
  // Create a unique key for this job's settings
  const settingsKey = `logViewer_${job.SchedulerName}_${job.Name}`;
  
  // Load settings from localStorage or use defaults
  const loadSettings = () => {
    try {
      const saved = localStorage.getItem(settingsKey);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  };
  
  // Save settings to localStorage
  const saveSettings = useCallback((settings) => {
    try {
      localStorage.setItem(settingsKey, JSON.stringify(settings));
    } catch (error) {
      console.warn('Failed to save LogViewer settings:', error);
    }
  }, [settingsKey]);
  
  const savedSettings = loadSettings();
  const [searchTerm, setSearchTerm] = useState(savedSettings.searchTerm || "");
  const [minimumLevel, setMinimumLevel] = useState(savedSettings.minimumLevel || "ALL");
  const [fullLog, setFullLog] = useState(savedSettings.fullLog || false);
  const [selectedLogFile, setSelectedLogFile] = useState(savedSettings.selectedLogFile || null);
  const [isPaused, setIsPaused] = useState(false); // Always start as running (not paused)

  const containerRef = useRef(null);
  const websocketRef = useRef(null);
  const isPausedRef = useRef(false); // Always initialize as false
  const isLoadingNewFileRef = useRef(false);

  // Update the ref whenever isPaused changes
  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  // Save settings whenever they change (except isPaused which should always start as false)
  useEffect(() => {
    saveSettings({
      searchTerm,
      minimumLevel,
      fullLog,
      selectedLogFile
    });
  }, [searchTerm, minimumLevel, fullLog, selectedLogFile, saveSettings]);

  // Log files are named "<ApplicationId>.log", so the job's current
  // ApplicationId identifies the log of the run in progress.
  const currentRunLogFile = job.ApplicationId ? `${job.ApplicationId}.log` : null;
  // Seeded with the run shown at mount time so restoring a saved selection is
  // not overridden; only a later run switches the viewer.
  const followedRunLogFileRef = useRef(currentRunLogFile);

  // Load log files list and restore selected file if available
  useEffect(() => {
    if (listLogFiles && listLogFiles.length > 0) {
      // Check if the previously selected file is still available
      if (selectedLogFile && listLogFiles.includes(selectedLogFile)) {
        // Keep the current selection as it's still valid
        return;
      } else {
        // Fallback to the latest log file
        setSelectedLogFile(listLogFiles[listLogFiles.length - 1]);
      }
    } else {
      setSelectedLogFile(null);
    }
  }, [listLogFiles, selectedLogFile]);

  // Follow a re-run: as soon as the refreshed list contains the log of the new
  // run, show it. The ref is only advanced once the switch actually happens, so
  // a job whose new ApplicationId arrives before the refreshed list is picked up
  // on the next list update instead of being missed.
  useEffect(() => {
    if (!currentRunLogFile) return;
    if (currentRunLogFile === followedRunLogFileRef.current) return;
    if (!listLogFiles || !listLogFiles.includes(currentRunLogFile)) return;
    followedRunLogFileRef.current = currentRunLogFile;
    setSelectedLogFile(currentRunLogFile);
  }, [currentRunLogFile, listLogFiles]);

  const buildWebSocketUrl = useCallback(() => {
    let url = `${BACKEND_WS_URL}/logs/?`;
    url += `schedulerName=${encodeURIComponent(job.SchedulerName)}`;
    url += `&jobName=${encodeURIComponent(job.Name)}`;
    url += `&full=${fullLog}`;
    url += `&isApp=${job.JobType === "Application"}`;
    if (selectedLogFile) {
      url += `&filename=${encodeURIComponent(selectedLogFile)}`;
    }
    return url;
  }, [fullLog, selectedLogFile, job.SchedulerName, job.Name, job.JobType]);

  const setupWebSocket = useCallback(() => {
    // The bearer travels in the Sec-WebSocket-Protocol header ("jwt", <token>)
    // instead of the URL query string, so it never lands in server access logs.
    const ws = new WebSocket(buildWebSocketUrl(), ["jwt", idToken]);
    ws.onopen = () => {
    };
    ws.onmessage = (event) => {
      try {
        const newLog = JSON.parse(event.data);
        // Add logs when not paused OR when loading a new file
        if (!isPausedRef.current || isLoadingNewFileRef.current) {
          setLogs((prevLogs) => [...prevLogs, newLog]);
          // After receiving first message of new file, turn off loading flag
          if (isLoadingNewFileRef.current) {
            // Allow a short time for initial file content to load
            setTimeout(() => {
              isLoadingNewFileRef.current = false;
            }, 1000);
          }
        }
      } catch (error) {
        console.error("WebSocket log - Error parsing message:", error);
      }
    };
    ws.onerror = (error) => {
      console.error("WebSocket log - error:", error);
    };
    ws.onclose = (event) => {
      // Only the current socket may trigger a reconnect; a socket replaced by
      // a newer one (file change, token refresh) or closed on unmount is stale.
      if (websocketRef.current !== ws) {
        return;
      }
      if (event.code === 4003) {
        // Authentication error (likely expired token)
        if (onAuthError) {
          // Let parent component handle the auth error (refresh, then logout)
          onAuthError("Your session has expired. Please log in again.");
        } else {
          // Fallback: try to reconnect in case onAuthError is not provided
          setTimeout(() => {
            if (websocketRef.current === ws) setupWebSocket();
          }, 2000);
        }
      } else if (event.code !== 1000) {
        // Abnormal close (e.g. network drop): reconnect so the live log
        // stream resumes instead of stopping silently.
        setTimeout(() => {
          if (websocketRef.current === ws) setupWebSocket();
        }, 5000);
      }
    };
    websocketRef.current = ws;
  }, [buildWebSocketUrl, onAuthError, idToken]);

  // Initialize the WebSocket
  useEffect(() => {
    setupWebSocket();
    return () => {
      if (websocketRef.current) {
        websocketRef.current.close();
        websocketRef.current = null;
      }
      // Clear saved settings when component unmounts (log section is collapsed)
      try {
        localStorage.removeItem(settingsKey);
      } catch (error) {
        console.warn('Failed to clear LogViewer settings:', error);
      }
    };
  }, [setupWebSocket, settingsKey]);

  // Reset logs and reconnect WebSocket when log file selection changes
  useEffect(() => {
    setLogs([]);
    // Set loading flag to allow initial content through even when paused
    isLoadingNewFileRef.current = true;
    if (websocketRef.current) {
      websocketRef.current.close();
    }
    setupWebSocket();
  }, [fullLog, selectedLogFile, setupWebSocket]);

  // Auto-scroll to bottom on new logs
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [logs]);

  const filteredLogs = useMemo(() => {
    const minLevelVal = LEVEL_PRIORITY[minimumLevel];
    return logs.filter((rawLog) => {
      const text = typeof rawLog === "string" ? rawLog : rawLog.text || "";
      const level = determineLogLevel(text);
      const logLevelVal = LEVEL_PRIORITY[level] ?? LEVEL_PRIORITY.DEBUG;
      const passLevel = logLevelVal >= minLevelVal;
      const passSearch =
        !searchTerm || text.toLowerCase().includes(searchTerm.toLowerCase());
      return passLevel && passSearch;
    });
  }, [logs, searchTerm, minimumLevel]);
  return (
    <div className="log-viewer">
      <div className="log-viewer-header">
        <div className="log-viewer-header-flexible">
          {listLogFiles && listLogFiles.length > 1 ? (
            <div className="log-viewer-file-selector">
              <select
                value={selectedLogFile || ""}
                onChange={(e) => setSelectedLogFile(e.target.value)}
                className="log-viewer-select"
              >
                {listLogFiles.map((file, index) => (
                  <option key={index} value={file}>
                    {file}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <span className="log-viewer-jobName">{job.Name}</span>
          )}
        </div>
        <div className="log-viewer-controls">
          <div className="log-level-buttons">
            {["ALL", "WARN", "ERROR"].map((option) => {
              const isActive = minimumLevel === option;
              return (
                <button
                  key={option}
                  className={isActive ? "active" : ""}
                  onClick={() => setMinimumLevel(option)}
                >
                  {option}
                </button>
              );
            })}
          </div>
          <input
            className="log-viewer-search"
            type="text"
            placeholder="Search logs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <label className="log-viewer-toggle">
            <input
              type="checkbox"
              checked={fullLog}
              onChange={(e) => setFullLog(e.target.checked)}
            />
            <span className="log-viewer-toggle-label">Full Log</span>
          </label>

          {/* Pause/Resume button */}
          <button
            className="log-viewer-pause-resume"
            onClick={() => setIsPaused(!isPaused)}
            aria-label={isPaused ? "Resume logs" : "Pause logs"}
          >
            {isPaused ? <FiPlay /> : <FiPause />}
          </button>

          {/* Expand entire CellJob rather than just logs */}
          <button
            className="log-viewer-maximize"
            onClick={onExpandClick}
            aria-label="Expand CellJob"
          >
            <FiMaximize2 />
          </button>
        </div>
      </div>

      <div className="log-viewer-container">
        <div ref={containerRef} className="log-viewer-body">
          {filteredLogs.map((rawLog, i) => {
            const text =
              typeof rawLog === "string" ? rawLog : rawLog.text || "";
            const level = determineLogLevel(text);
            const color = LOG_LEVELS[level]?.color || "#aaa";

            return (
              <div key={i} className="log-entry">
                <span className="log-text" style={{ color }}>
                  {text}
                </span>
              </div>
            );
          })}
          <div className="log-entry">
            <span className="log-text">&nbsp;</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogViewer;
